import type { Dir, SkinDef, ThemeDef } from '@snakefeast/shared';

/**
 * Snake and arena artwork, shared by the game renderer and the cosmetic
 * previews (shop, settings), so what people buy looks exactly like what they get.
 * Everything is plain Canvas 2D: layered strokes for the body (outline, base,
 * pattern, gloss), a tapered tail, an animated head, and tiled theme textures.
 */

export type Pt = [number, number];

// ----- color helpers -----

function parse(c: string): [number, number, number] {
  let h = c.replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const n = parseInt(h.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export const hex = (r: number, g: number, b: number) => `#${[r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
export function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  return hex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
export const lighten = (c: string, t: number) => mix(c, '#ffffff', t);
export const darken = (c: string, t: number) => mix(c, '#000000', t);
export function rgba(c: string, a: number): string {
  const [r, g, b] = parse(c);
  return `rgba(${r},${g},${b},${a})`;
}
export const isLight = (c: string) => {
  const [r, g, b] = parse(c);
  return r * 0.299 + g * 0.587 + b * 0.114 > 150;
};

/** Small deterministic hash, so patterns stay put on the same segment. */
const hash = (n: number) => {
  let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
};

// ----- snake -----

export interface SnakeShape {
  /** Body centre line: one point per cell, head first, one cell apart. */
  pts: Pt[];
  /** Indices i where pts[i-1] -> pts[i] is not connected (wrap-around, teleport). */
  breaks?: Set<number>;
  /** Fractional index of the head along `pts` (it glides between cells). Default 0. */
  front?: number;
  /** Fractional index of the tail tip. Default the last point. */
  back?: number;
  /**
   * Only this stretch (fractional indices) needs drawing: the rest is off screen. In a big arena
   * most of a long snake is, and filling its whole body for every layer was most of the work.
   */
  clip?: [number, number];
}

export interface SnakeArtOptions {
  skin: SkinDef;
  /** Cell size in CSS px. */
  cs: number;
  quality: 'high' | 'low';
  reduceMotion: boolean;
  now: number;
  /** Where the head looks. */
  dir: Dir;
  /** Unit vector the head looks along, overriding `dir` (lets the head swing round a turn instead of snapping). */
  heading?: Pt;
  /** Soft glow under the body (your own snake, neon skins). */
  glow: boolean;
  /** Light rim around the whole body, in every quality, so your own snake stands out on dark arenas. */
  highlight?: boolean;
  /** Extra colored halo along the body (power-up effects). */
  aura?: { color: string; alpha: number; width: number };
  /** Stable per-snake number for blinks/tongue timing. */
  seed?: number;
  /** Cells the skin pattern has slid toward the head. Shop previews animate it; in game the body itself moves. */
  flow?: number;
  /** Fine scale texture on skins without their own pattern (default on). The game keeps it for your own snake: it costs a lot to draw. */
  texture?: boolean;
  /** Fire power-up: flames lick up off the whole body. */
  burning?: boolean;
  /** How far the jaws are open, 0..1: they part as food comes up ahead and snap shut on it. */
  mouth?: number;
  /** Swallowed food on its way down: each a distance (cells) back from the head, and its size 0..1. */
  bulges?: ReadonlyArray<{ d: number; size: number }>;
  /** Unit vector the eyes look along (food nearby); default the heading. */
  look?: Pt;
  /** How far through a chomp the head is (0..1, see CHOMP_MS), after eating; undefined when not eating. */
  chomp?: number;
}

const DX = [0, 1, 0, -1];
const DY = [-1, 0, 1, 0];
/** Cells over which the tail narrows to a point. */
const TAPER = 6;
/** How wide the tail's tip is, as a share of the body (then rounded off). */
const TAIL_TIP = 0.26;
/** Outline points along the tail: its width follows a curve there, not a straight line. */
const TAPER_STEPS = 6;
/** Tail width at x (0 at the tip, 1 where the taper starts): stays full, then narrows to the point, like a real tail. */
const taperCurve = (x: number) => x * (2 - x);
/**
 * Body width as a share of a cell. Well under a cell, so ground shows between rows: a snake doubling back
 * on itself, or two snakes side by side, read as apart (at 0.94 they looked stuck together, and a player
 * would ask why touching snakes didn't die).
 */
const BODY = 0.64;
/** Head radius as a share of a cell: a little wider than the body (the viper's; its outline flares wider). */
const HEAD = 0.52;
/** The round head's radius: wider than the body, so the snake has a face, not just an end, but not over the next row. */
const ROUND_HEAD_R = 0.525;
/**
 * The outline round the body, as a share of a cell on top of its width, and how far toward black its color
 * goes. A light hand on both: a near-black outline matched the dark arenas and filled the gap between rows.
 */
const OUTLINE = 0.06;
const OUTLINE_SHADE = 0.32;
/** Neon skins' soft glow, times the body's width: inside the cell, for the same reason. */
const GLOW = 1.1;
/** Your own snake's bright rim, as a share of a cell on top of the body's width. */
const OWN_RIM = 0.12;
/** The drop shadow under body and head: how far down and right it falls (shares of a cell), and how dark. Close, so it doesn't fill the gap below. */
const SHADOW_X = 0.03;
const SHADOW_Y = 0.07;
const SHADOW_ALPHA = 0.22;

/** Skins people unlock or buy get a glint running down the body, so they look worth it. */
const isFancy = (s: SkinDef) => s.premium || !!(s.price || s.pass || s.season || s.missionDays || s.loginStreak);

/** A turn's arc as an outline needs it: centre, radius, and the cell centre it rounds. */
interface TurnRef {
  cx: number;
  cy: number;
  r: number;
  ax: number;
  ay: number;
}

/** Where a point on the centre line is, and which way the head is from there (unit vector). */
interface Place {
  x: number;
  y: number;
  dx: number;
  dy: number;
}

/** Outline samples split into runs at breaks: run r is seq[starts[r]] up to seq[starts[r + 1]] (or the end). */
interface Runs {
  seq: Sample[];
  starts: number[];
}

/** A point on the centre line used to outline the body. */
interface Sample extends Place {
  u: number;
  /** Turns at or next to this point: a wide outline's inner edge can reach that far. */
  turns: TurnRef[];
  /** The line breaks (wrap-around edge) just before this point. */
  brk?: boolean;
}

/**
 * The body's centre line as a smooth curve: straight between cell centres, and every turn a
 * quarter circle from one half-cell to the next. Points are addressed by fractional body index,
 * so the head glides round a corner instead of turning on the spot, and patterns can sit
 * anywhere along the body and travel with it.
 *
 * It only depends on the cells, which change once a tick, so it is cached by the points array
 * and reused every frame in between: drawing a snake then allocates almost nothing, which keeps
 * garbage-collection pauses (felt as stutter) off the frame loop.
 */
class Geometry {
  readonly n: number;
  private turns: Array<{ cx: number; cy: number; r: number; a: number; d: number } | null | undefined> = [];
  private nearTurns: Array<TurnRef[] | undefined> = [];
  private sampled = new Map<number, Sample[]>();
  /** Reused every frame for the body's outline samples. */
  readonly bodyRuns: Runs = { seq: [], starts: [] };

  constructor(
    readonly pts: Pt[],
    readonly breaks: Set<number> | undefined,
  ) {
    this.n = pts.length;
  }

  linked(i: number, j: number): boolean {
    return j >= 0 && j < this.n && !this.breaks?.has(Math.max(i, j));
  }

  /** The arc round the turn at point i (centre, radius, start angle on the head side, signed sweep); null when straight. */
  turn(i: number) {
    let t = this.turns[i];
    if (t !== undefined) return t;
    t = null;
    if (this.linked(i, i - 1) && this.linked(i, i + 1)) {
      const [x, y] = this.pts[i]!;
      const [px, py] = this.pts[i - 1]!;
      const [nx, ny] = this.pts[i + 1]!;
      if (Math.abs((x - px) * (ny - y) - (y - py) * (nx - x)) > 1e-6) {
        const m1x = (x + px) / 2;
        const m1y = (y + py) / 2;
        const m2x = (x + nx) / 2;
        const m2y = (y + ny) / 2;
        // The corner of the square opposite the cell centre.
        const cx = m1x + m2x - x;
        const cy = m1y + m2y - y;
        const a = Math.atan2(m1y - cy, m1x - cx);
        const b = Math.atan2(m2y - cy, m2x - cx);
        t = { cx, cy, r: Math.hypot(m1x - cx, m1y - cy), a, d: Math.atan2(Math.sin(b - a), Math.cos(b - a)) };
      }
    }
    this.turns[i] = t;
    return t;
  }

  /** Turns at point i and its linked neighbours. */
  near(i: number): TurnRef[] {
    let list = this.nearTurns[i];
    if (list) return list;
    list = [];
    for (const j of [i, i - 1, i + 1]) {
      const tr = j === i || this.linked(i, j) ? this.turn(j) : null;
      if (tr) list.push({ cx: tr.cx, cy: tr.cy, r: tr.r, ax: this.pts[j]![0], ay: this.pts[j]![1] });
    }
    this.nearTurns[i] = list;
    return list;
  }

  /** Which point's half-cell span u falls in. */
  span(u: number): number {
    return Math.max(0, Math.min(this.n - 1, Math.floor(u + 0.5)));
  }

  /** Write the point at offset t (-0.5 toward the head .. 0.5 toward the tail) around point i, and the way to the head, into `out`. */
  place(i: number, t: number, out: Place) {
    const tr = this.turn(i);
    if (tr) {
      const ang = tr.a + tr.d * (t + 0.5);
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const s = tr.d > 0 ? 1 : -1;
      out.x = tr.cx + cos * tr.r;
      out.y = tr.cy + sin * tr.r;
      out.dx = sin * s;
      out.dy = -cos * s;
      return;
    }
    const p = this.pts[i]!;
    const x = p[0];
    const y = p[1];
    let ox = 0;
    let oy = -1;
    if (this.linked(i, i - 1)) {
      ox = this.pts[i - 1]![0] - x;
      oy = this.pts[i - 1]![1] - y;
    } else if (this.linked(i, i + 1)) {
      ox = x - this.pts[i + 1]![0];
      oy = y - this.pts[i + 1]![1];
    }
    const l = Math.sqrt(ox * ox + oy * oy) || 1;
    out.dx = ox / l;
    out.dy = oy / l;
    out.x = x;
    out.y = y;
    if (!t) return;
    const k = Math.abs(t);
    const toward = t < 0 ? i - 1 : i + 1;
    const away = t < 0 ? i + 1 : i - 1;
    if (this.linked(i, toward)) {
      out.x = x + (this.pts[toward]![0] - x) * k;
      out.y = y + (this.pts[toward]![1] - y) * k;
    } else if (this.linked(i, away)) {
      // Across a break (wrap-around edge): carry straight on out of the cell instead of jumping across the map.
      out.x = x - (this.pts[away]![0] - x) * k;
      out.y = y - (this.pts[away]![1] - y) * k;
    }
  }

  sample(u: number, brk = false): Sample {
    const i = this.span(u);
    const s: Sample = { u, x: 0, y: 0, dx: 0, dy: 0, turns: this.near(i), brk };
    this.place(i, u - i, s);
    return s;
  }

  /**
   * Samples along the whole path: the ends of every half-cell span and `arcSteps` points round
   * each turn. That outlines the body exactly, since along a straight stretch the width only
   * changes linearly. A break gets a sample on each side (the second one marked `brk`).
   */
  samples(arcSteps: number): Sample[] {
    let out = this.sampled.get(arcSteps);
    if (out) return out;
    out = [];
    const push = (i: number, u: number, brk = false) => {
      const s: Sample = { u, x: 0, y: 0, dx: 0, dy: 0, turns: this.near(i), brk };
      this.place(i, u - i, s);
      out!.push(s);
    };
    for (let i = 0; i < this.n; i++) {
      const lo = Math.max(0, i - 0.5);
      const hi = Math.min(this.n - 1, i + 0.5);
      const brk = i > 0 && !!this.breaks?.has(i);
      if (!i || brk) push(i, lo, brk);
      if (this.turn(i)) for (let k = 1; k < arcSteps; k++) push(i, i - 0.5 + k / arcSteps);
      if (hi > lo) push(i, hi);
    }
    this.sampled.set(arcSteps, out);
    return out;
  }
}

const geometries = new WeakMap<Pt[], Geometry>();

/** The cached geometry for a points array (rebuilt if the breaks changed). */
function geometryOf(pts: Pt[], breaks: Set<number> | undefined): Geometry {
  let g = geometries.get(pts);
  if (!g || g.breaks !== breaks) {
    g = new Geometry(pts, breaks);
    geometries.set(pts, g);
  }
  return g;
}

/** One frame's view of a snake: its geometry, plus where the head and tail tip are along it. */
class Curve {
  readonly front: number;
  readonly back: number;
  /** Cells over which the tail narrows (shorter on a short snake). */
  readonly taperLen: number;
  /** Points round each turn when outlining the body (fewer in battery saver). */
  arcSteps = 8;
  /** The stretch that is on screen (see SnakeShape.clip): the body and its marks are drawn over this only. */
  readonly lo: number;
  readonly hi: number;
  private bodyDone = false;
  readonly g: Geometry;

  constructor(s: SnakeShape) {
    this.g = geometryOf(s.pts, s.breaks);
    const n = this.g.n;
    this.back = Math.max(0, Math.min(n - 1, s.back ?? n - 1));
    this.front = Math.max(0, Math.min(this.back, s.front ?? 0));
    // At most under half a short snake, so a new player's snake is a body with a tail, not all tail.
    this.taperLen = Math.min(TAPER, (this.back - this.front) * 0.45);
    this.lo = Math.max(this.front, s.clip?.[0] ?? this.front);
    this.hi = Math.min(this.back, s.clip?.[1] ?? this.back);
  }

  /** Write the point at u and the way to the head into `out` (no allocation). */
  place(u: number, out: Place) {
    const i = this.g.span(u);
    this.g.place(i, u - i, out);
  }

  pos(u: number): Pt {
    this.place(u, scratch);
    return [scratch.x, scratch.y];
  }

  /** Unit vector along the body at u, pointing toward the head. */
  dir(u: number): Pt {
    this.place(u, scratch);
    return [scratch.dx, scratch.dy];
  }

  /** Samples of the whole body, built once per frame (into buffers the geometry keeps) and shared by every layer. */
  body(): Runs {
    if (!this.bodyDone) this.runs(this.lo, Math.max(this.lo, this.hi), this.g.bodyRuns);
    this.bodyDone = true;
    return this.g.bodyRuns;
  }

  /**
   * Samples from u0 to u1, split into runs at breaks: the cached path samples in between, plus
   * the two ends and the start of the tail taper, which move every frame.
   */
  runs(u0: number, u1: number, out: Runs): Runs {
    const all = this.g.samples(this.arcSteps);
    const seq = out.seq;
    const starts = out.starts;
    seq.length = 0;
    starts.length = 0;
    starts.push(0);
    seq.push(this.g.sample(u0));
    // The tail's own points (TAPER_STEPS of them from where it starts narrowing), merged in as the samples pass them.
    const taperFrom = this.back - this.taperLen;
    const tStep = this.taperLen / TAPER_STEPS;
    let tp = 0;
    while (tp < TAPER_STEPS && taperFrom + tp * tStep <= u0) tp++;
    let lo = 0;
    let hi = all.length;
    while (lo < hi) {
      const m = (lo + hi) >> 1;
      if (all[m]!.u <= u0) lo = m + 1;
      else hi = m;
    }
    for (let k = lo; k < all.length && all[k]!.u < u1; k++) {
      const s = all[k]!;
      while (tp < TAPER_STEPS && taperFrom + tp * tStep <= s.u) {
        const u = taperFrom + tp++ * tStep;
        if (u < s.u) seq.push(this.g.sample(u));
      }
      if (s.brk) {
        // A run of one point has nothing to outline: start the next one in its place.
        if (seq.length - starts[starts.length - 1]! > 1) starts.push(seq.length);
        else seq.length = starts[starts.length - 1]!;
      }
      seq.push(s);
    }
    while (tp < TAPER_STEPS && taperFrom + tp * tStep < u1) {
      const u = taperFrom + tp++ * tStep;
      if (u > u0) seq.push(this.g.sample(u));
    }
    seq.push(this.g.sample(u1));
    return out;
  }

  /** Add the centre line from u0 to u1 to the current path (a new subpath, split at breaks). */
  trace(ctx: CanvasRenderingContext2D, u0: number, u1: number) {
    const g = this.g;
    const first = g.span(u0);
    const last = g.span(u1);
    g.place(first, u0 - first, scratch);
    ctx.moveTo(scratch.x, scratch.y);
    for (let i = first; i <= last; i++) {
      const lo = Math.max(u0, i - 0.5) - i;
      const hi = Math.min(u1, i + 0.5) - i;
      if (i > first && g.breaks?.has(i)) {
        g.place(i, lo, scratch);
        ctx.moveTo(scratch.x, scratch.y);
      }
      if (hi <= lo) continue;
      const tr = g.turn(i);
      if (tr) ctx.arc(tr.cx, tr.cy, tr.r, tr.a + tr.d * (lo + 0.5), tr.a + tr.d * (hi + 0.5), tr.d < 0);
      else {
        g.place(i, hi, scratch);
        ctx.lineTo(scratch.x, scratch.y);
      }
    }
  }

  /** Width multiplier at u: 1 along the body, narrowing over the tail down to `to`. */
  taper(u: number, to = TAIL_TIP): number {
    if (this.taperLen <= 0) return 1;
    const d = this.back - u;
    return d >= this.taperLen ? 1 : to + (1 - to) * taperCurve(Math.max(0, d) / this.taperLen);
  }

  /**
   * Calls fn(u, id) every `spacing` cells from the head, slid toward the head by `flow`
   * (starting up to one spacing before the head, so a mark can overlap it).
   * `id` stays with the same mark as it slides, so it can seed per-mark looks.
   */
  marks(spacing: number, flow: number, fn: (u: number, id: number) => void) {
    // Marks a cell or so outside the on-screen stretch still show at its edge.
    const from = Math.max(this.front - spacing, this.lo - 1);
    const to = Math.min(this.back, this.hi + 1);
    for (let m = Math.floor((from - this.front + flow) / spacing - 0.5); ; m++) {
      const u = this.front + (m + 0.5) * spacing - flow;
      if (u > to) break;
      if (u >= from) fn(u, m);
    }
  }
}

/** Reused out-parameter for points along a body, so the per-frame drawing code doesn't allocate. */
const scratch: Place = { x: 0, y: 0, dx: 0, dy: 0 };

/** How long point i's span of the centre line is, in cells: 1 straight, about 0.79 round a corner. */
function spanLength(g: Geometry, i: number, cs: number): number {
  const t = g.turn(i);
  return t ? (Math.abs(t.d) * t.r) / cs : 1;
}

/** Distance along the centre line from the head end (u = 0) to u, in cells. */
export function snakeDistance(s: SnakeShape, u: number, cs: number): number {
  const g = geometryOf(s.pts, s.breaks);
  let d = 0;
  for (let i = 0; i < g.n; i++) {
    const lo = Math.max(0, i - 0.5);
    const hi = i === g.n - 1 ? u : Math.min(u, i + 0.5);
    if (hi > lo) d += (hi - lo) * spanLength(g, i, cs);
    if (u <= i + 0.5) break;
  }
  return d;
}

/** The fractional index at a distance (cells) along the centre line from the head end: the inverse of snakeDistance. */
export function snakeIndexAt(s: SnakeShape, dist: number, cs: number): number {
  const g = geometryOf(s.pts, s.breaks);
  let d = 0;
  for (let i = 0; i < g.n; i++) {
    const lo = Math.max(0, i - 0.5);
    const f = spanLength(g, i, cs);
    const len = (i + 0.5 - lo) * f;
    if (d + len >= dist || i === g.n - 1) return lo + (dist - d) / f;
    d += len;
  }
  return dist;
}

/** A point on a snake's centre line at fractional index u. */
export function snakePoint(s: SnakeShape, u: number): Pt {
  return new Curve(s).pos(u);
}

/**
 * Which way a head at u should look: along the chord from `back` cells behind it to `front`
 * cells ahead (never past the real head at 0). Like the tangent, but a corner turns the head
 * over 1 + front + back cells instead of one, speeding up and slowing down gently, so the head
 * swings round smoothly instead of snapping 90 degrees in a few frames. Falls back to the
 * tangent where the chord would cross a break (wrap-around edge).
 */
export function snakeHeading(s: SnakeShape, u: number, front: number, back: number): Pt {
  const c = new Curve(s);
  const ahead = Math.max(0, u - front);
  const behind = Math.min(c.back, u + back);
  const g = c.g;
  for (let i = g.span(ahead) + 1; i <= g.span(behind); i++) if (g.breaks?.has(i)) return c.dir(u);
  c.place(ahead, scratch);
  const ax = scratch.x;
  const ay = scratch.y;
  c.place(behind, scratch);
  const dx = ax - scratch.x;
  const dy = ay - scratch.y;
  const l = Math.sqrt(dx * dx + dy * dy);
  return l > 1e-6 ? [dx / l, dy / l] : c.dir(u);
}

/** Points along a snake's centre line, head to tail, every `step` cells. */
export function snakeSamples(s: SnakeShape, step: number): Pt[] {
  const c = new Curve(s);
  const out: Pt[] = [];
  for (let u = c.front; u < c.back; u += step) out.push(c.pos(u));
  out.push(c.pos(c.back));
  return out;
}

/** Right-hand edge points of the outline being built, reused so filling allocates nothing. */
let rightEdge = new Float64Array(512);

/**
 * Fill the body's outline over the sampled runs at `width`, narrowing over the tail to a round
 * tip. One fill per layer: no seams between pieces, and where the snake crosses itself (or a
 * turn is tighter than the outline is wide) a translucent layer still covers each spot once.
 *
 * This runs for every point of every layer of every snake each frame, so it is written to not
 * allocate: everything is inlined into locals (a fractional number returned from a call, or kept
 * in a module-level variable, gets boxed on the heap, and that garbage adds up to stutter).
 */
function fillRuns(ctx: CanvasRenderingContext2D, c: Curve, runs: Runs, width: number, taperTo: number) {
  const { seq, starts } = runs;
  const back = c.back;
  const taperLen = c.taperLen;
  const half = width / 2;
  if (rightEdge.length < seq.length * 2) rightEdge = new Float64Array(seq.length * 4);
  const right = rightEdge;
  ctx.beginPath();
  for (let r = 0; r < starts.length; r++) {
    const from = starts[r]!;
    const to = r + 1 < starts.length ? starts[r + 1]! : seq.length;
    if (to - from < 2) continue;
    let h = half;
    for (let i = from; i < to; i++) {
      const s = seq[i]!;
      const d = back - s.u;
      if (taperLen <= 0 || d >= taperLen) h = half;
      else {
        const x = Math.max(0, d) / taperLen;
        // taperCurve, inlined.
        h = half * (taperTo + (1 - taperTo) * x * (2 - x));
      }
      let lx = s.x - s.dy * h;
      let ly = s.y + s.dx * h;
      let rx = s.x + s.dy * h;
      let ry = s.y - s.dx * h;
      // Inside a turn tighter than this outline is wide: stop at the corner where the two straight
      // edges meet, as a stroke would, instead of looping back on itself (which leaves slivers).
      const turns = s.turns;
      let leftDone = false;
      let rightDone = false;
      for (let q = 0; q < turns.length; q++) {
        const t = turns[q]!;
        const ox = s.x - t.ax;
        const oy = s.y - t.ay;
        if (h <= t.r || ox * ox + oy * oy >= h * h) continue;
        const k = h / t.r;
        const cx = t.ax + (t.cx - t.ax) * k;
        const cy = t.ay + (t.cy - t.ay) * k;
        if (-s.dy * (t.cx - s.x) + s.dx * (t.cy - s.y) > 0) {
          if (!leftDone) {
            lx = cx;
            ly = cy;
            leftDone = true;
          }
        } else if (!rightDone) {
          rx = cx;
          ry = cy;
          rightDone = true;
        }
      }
      if (i > from) ctx.lineTo(lx, ly);
      else ctx.moveTo(lx, ly);
      right[(i - from) * 2] = rx;
      right[(i - from) * 2 + 1] = ry;
    }
    const end = seq[to - 1]!;
    if (end.u >= back - 1e-6) {
      // Round tip: half a circle from the left edge, round the end, to the right edge.
      const a = Math.atan2(end.dx, -end.dy);
      ctx.arc(end.x, end.y, h, a, a + Math.PI);
    }
    for (let i = to - 1 - from; i >= 0; i--) ctx.lineTo(right[i * 2]!, right[i * 2 + 1]!);
    ctx.closePath();
  }
  ctx.fill();
}

function strokeBody(ctx: CanvasRenderingContext2D, c: Curve, width: number, style: string | CanvasGradient, taperTo = TAIL_TIP) {
  ctx.fillStyle = style;
  fillRuns(ctx, c, c.body(), width, taperTo);
}

/** How far (in body widths) the lit side of the tube sits toward the light, which comes from the top left. */
const LIGHT_SHIFT_X = 0.05;
const LIGHT_SHIFT_Y = 0.065;
/** How far the shadow tone sits toward the light, uncovering the light rim along the far edge. */
const RIM_SHIFT_X = 0.035;
const RIM_SHIFT_Y = 0.045;

export function drawSnake(ctx: CanvasRenderingContext2D, shape: SnakeShape | Pt[], o: SnakeArtOptions): void {
  const s = Array.isArray(shape) ? { pts: shape } : shape;
  if (!s.pts.length) return;
  const c = new Curve(s);
  const { skin, cs } = o;
  const high = o.quality === 'high';
  const w = cs * BODY;
  c.arcSteps = high ? 8 : 4;
  const [hx, hy] = c.pos(c.front);
  ctx.lineJoin = 'round';

  if (c.back > c.front) {
    const a = ctx.globalAlpha;
    if (high) {
      // Soft drop shadow: lifts the snake off the ground.
      ctx.save();
      ctx.translate(cs * SHADOW_X, cs * SHADOW_Y);
      ctx.globalAlpha = a * SHADOW_ALPHA;
      strokeBody(ctx, c, w, '#000000');
      ctx.restore();
    }
    if (o.aura) {
      ctx.globalAlpha = a * o.aura.alpha;
      strokeBody(ctx, c, w * o.aura.width, o.aura.color, 0.6);
    } else if (o.glow && high) {
      ctx.globalAlpha = a * 0.2;
      strokeBody(ctx, c, w * GLOW, skin.glow, 0.6);
    }
    if (o.highlight) {
      // Your own snake: a thin bright rim just outside its outline. A wide halo used to fill the gap
      // between its rows; the marker over its head and the camera on it already say which one is yours.
      ctx.globalAlpha = a * 0.3;
      strokeBody(ctx, c, w + Math.max(2, cs * OWN_RIM), '#ffffff');
    }
    ctx.globalAlpha = a;
    // Outline, then the base color.
    strokeBody(ctx, c, w + Math.max(1, cs * OUTLINE), darken(skin.body[1], OUTLINE_SHADE));
    const [tx, ty] = c.pos(c.back);
    // The detail layers below that cost a full fill or a mark per cell: in game only for your own snake.
    const detail = high && o.texture !== false;
    if (high) {
      // 3D, lit from the top left: the whole tube in its shadow tone, then the lit color slightly narrower
      // and nudged toward the light, leaving a dark crescent along the far edge, as on a lit cylinder.
      // With detail, a light rim goes under the shadow tone first (light bouncing back off the ground onto
      // the far edge), and the shadow tone goes on a little narrower and nudged, leaving it as a thin bright
      // line along the bottom right.
      ctx.save();
      if (detail) {
        const bounce = ctx.createLinearGradient(hx, hy, tx, ty);
        bounce.addColorStop(0, lighten(skin.body[0], 0.3));
        bounce.addColorStop(1, lighten(skin.body[1], 0.22));
        strokeBody(ctx, c, w, bounce);
        ctx.translate(-w * RIM_SHIFT_X, -w * RIM_SHIFT_Y);
      }
      const shade = ctx.createLinearGradient(hx, hy, tx, ty);
      shade.addColorStop(0, darken(skin.body[0], 0.4));
      shade.addColorStop(1, darken(skin.body[1], 0.4));
      strokeBody(ctx, c, detail ? w * 0.9 : w, shade);
      ctx.translate(-w * (LIGHT_SHIFT_X - (detail ? RIM_SHIFT_X : 0)), -w * (LIGHT_SHIFT_Y - (detail ? RIM_SHIFT_Y : 0)));
    }
    const lit = high ? w * 0.8 : w;
    if (skin.pattern === 'rainbow' && skin.colors?.length) drawBands(ctx, c, skin.colors, lit, o.flow ?? 0);
    else {
      const grad = ctx.createLinearGradient(hx, hy, tx, ty);
      grad.addColorStop(0, skin.body[0]);
      grad.addColorStop(1, skin.body[1]);
      strokeBody(ctx, c, lit, grad);
    }
    if (high) {
      // A lighter core, in two soft steps, on the lit side: the body reads as a round tube instead of a flat ribbon.
      const core = lighten(skin.body[0], 0.4);
      ctx.globalAlpha = a * 0.2;
      strokeBody(ctx, c, w * 0.56, core);
      ctx.globalAlpha = a * 0.22;
      strokeBody(ctx, c, w * 0.28, core);
      ctx.globalAlpha = a;
      ctx.restore();
    }
    if (o.bulges?.length) drawBulges(ctx, c, o, w);
    if (skin.pattern !== 'neon') drawSegments(ctx, c, o, w, detail);
    // Fine scale texture on skins without their own markings (your own snake in game: it costs a lot to draw).
    if (detail && skin.pattern === 'solid') {
      drawScales(ctx, c, w, o.flow ?? 0, darken(skin.body[1], 0.35), a * 0.1, 0.34);
    }
    drawPattern(ctx, c, o, w);
    // Gloss: a thin light line along the top of the tube, where the light hits it.
    if (high) {
      ctx.globalAlpha = a * 0.5;
      ctx.save();
      ctx.translate(-w * 0.15, -w * 0.19);
      strokeBody(ctx, c, w * 0.16, lighten(skin.head, 0.7), 0.1);
      ctx.restore();
      ctx.globalAlpha = a;
    }
    if (isFancy(skin)) {
      // Signature skins have a living pattern of their own; glitter on top of it was just noise.
      if (!skin.signature) drawGlitter(ctx, c, o, w);
      if (!o.reduceMotion) drawSheen(ctx, c, o, w);
    }
    if (o.burning && !o.reduceMotion) drawEmbers(ctx, c, o, w, 0.5, 1.6);
  }
  drawHead(ctx, hx, hy, o);
}

/**
 * The body's segments, one a cell: a dark seam across the body at the back of each, bowed toward the tail
 * like overlapping plates, and (with `beads`) a glossy bead of light on each one's lit side. They ride along
 * with the body, so the snake is seen to crawl, and give every skin a built, detailed look. Each layer is one
 * path, and the seams are thin filled crescents: cheaper to draw than stroked curves.
 */
function drawSegments(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number, beads: boolean) {
  const { skin } = o;
  const a = ctx.globalAlpha;
  const flow = o.flow ?? 0;
  const clear = (u: number) => u > c.front + 0.75 && u < c.back - 0.6;
  ctx.fillStyle = darken(skin.body[1], 0.55);
  ctx.globalAlpha = a * 0.42;
  ctx.beginPath();
  // Seams between the cells (where rainbow bands change color).
  c.marks(1, flow, (m) => {
    const u = m + 0.5;
    if (!clear(u)) return;
    c.place(u, scratch);
    const { x, y, dx, dy } = scratch;
    const k = c.taper(u);
    const half = w * 0.38 * k;
    const bow = w * 0.4 * k;
    const thick = Math.max(1, w * 0.1 * k);
    const lx = x - dy * half + dx * bow * 0.15;
    const ly = y + dx * half + dy * bow * 0.15;
    ctx.moveTo(lx, ly);
    ctx.quadraticCurveTo(x - dx * bow, y - dy * bow, x + dy * half + dx * bow * 0.15, y - dx * half + dy * bow * 0.15);
    ctx.quadraticCurveTo(x - dx * (bow - thick), y - dy * (bow - thick), lx, ly);
  });
  ctx.fill();
  if (beads) {
    // Beads: light from the top left, so the same screen offset on every segment.
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = a * 0.2;
    ctx.beginPath();
    c.marks(1, flow, (u) => {
      if (!clear(u)) return;
      c.place(u, scratch);
      const { x, y, dx, dy } = scratch;
      const k = c.taper(u);
      const bx = x - w * 0.12 * k;
      const by = y - w * 0.15 * k;
      const rx = w * 0.26 * k;
      ctx.moveTo(bx + dx * rx, by + dy * rx);
      ctx.ellipse(bx, by, rx, w * 0.13 * k, Math.atan2(dy, dx), 0, Math.PI * 2);
    });
    ctx.fill();
  }
  ctx.globalAlpha = a;
}

/**
 * A swallowed meal moving down the body: the body swells smoothly around it, over about two cells,
 * outlined like the body and in its color there, under the pattern so the markings run over it.
 */
function drawBulges(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const { skin, cs } = o;
  const a = ctx.globalAlpha;
  const span = Math.max(1, c.back - c.front);
  const rim = Math.max(2, cs * 0.12) / 2;
  // The swell's profile: discs every 0.2 cell, sized on a bell curve around the meal.
  const at = (b: { d: number; size: number }, off: number) => {
    const u = c.front + b.d + off;
    if (u <= c.front + 0.3 || u >= c.back - 0.3) return null;
    // Off screen (see SnakeShape.clip): nothing to draw.
    if (u < c.lo - 1 || u > c.hi + 1) return null;
    c.place(u, scratch);
    const r = (w / 2) * c.taper(u) * (1 + BULGE_SWELL * b.size * Math.exp(-((off / 0.6) ** 2)));
    return { x: scratch.x, y: scratch.y, r, u };
  };
  for (const b of o.bulges!) {
    if (b.size <= 0) continue;
    const u = c.front + b.d;
    if (u < c.lo - 2 || u > c.hi + 2) continue;
    for (const pass of [0, 1] as const) {
      ctx.fillStyle = pass === 0 ? darken(skin.body[1], 0.55) : mix(skin.body[0], skin.body[1], Math.min(1, b.d / span));
      ctx.beginPath();
      for (let off = -1.2; off <= 1.21; off += 0.2) {
        const d = at(b, off);
        if (!d) continue;
        const r = d.r + (pass === 0 ? rim : 0);
        ctx.moveTo(d.x + r, d.y);
        ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
      }
      ctx.fill();
    }
    // A soft highlight on the lit side of the swell, so it reads as round.
    const mid = at(b, 0);
    if (mid) {
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = a * 0.14 * b.size;
      ctx.beginPath();
      ctx.arc(mid.x - w * 0.1, mid.y - w * 0.12, mid.r * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = a;
    }
  }
}

/** How much wider the body gets at the middle of a swallowed meal (a share of its width). */
const BULGE_SWELL = 0.6;

/** Reused for rainbow bands, which are filled as soon as they're sampled. */
const bandRuns: Runs = { seq: [], starts: [] };

/** Rainbow skins: one color per cell, counted from the head, so the bands travel with the snake. */
function drawBands(ctx: CanvasRenderingContext2D, c: Curve, colors: string[], w: number, flow: number) {
  c.marks(1, flow, (u, id) => {
    const lo = Math.max(c.front, u - 0.5);
    const hi = Math.min(c.back, u + 0.5);
    if (hi <= lo) return;
    ctx.fillStyle = colors[((id % colors.length) + colors.length) % colors.length]!;
    fillRuns(ctx, c, c.runs(lo, hi, bandRuns), w, TAIL_TIP);
  });
}

/**
 * Glitter: tiny four-point sparkles that wink on and off all over the body, so every snake
 * looks shiny. Each sparkle has its own spot and rhythm and rides along with the body. All of
 * them go into one path and one fill, so it stays cheap on long snakes.
 */
function drawGlitter(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const a = ctx.globalAlpha;
  const t = o.now / 1700;
  const seed = (o.seed ?? 0) * 101;
  ctx.fillStyle = '#ffffff';
  ctx.globalAlpha = a;
  ctx.beginPath();
  c.marks(o.quality === 'high' ? 0.4 : 0.8, o.flow ?? 0, (u, id) => {
    if (u <= c.front + 0.5 || u >= c.back - 0.3) return;
    const h1 = hash(id * 17 + seed);
    const h2 = hash(id * 29 + seed + 7);
    let k: number;
    if (o.reduceMotion) {
      // Still: a few sparkles that stay lit.
      if (h2 > 0.18) return;
      k = 0.75;
    } else {
      // Each sparkle is lit for a third of its cycle: in, peak, out.
      const phase = (t * (0.8 + h1 * 0.5) + h2) % 1;
      if (phase > 0.33) return;
      k = Math.sin((phase / 0.33) * Math.PI);
    }
    c.place(u, scratch);
    const { x, y, dx, dy } = scratch;
    const taper = c.taper(u);
    const across = (h1 - 0.5) * w * 0.62 * taper;
    const sx = x - dy * across;
    const sy = y + dx * across;
    const r = w * (0.15 + h2 * 0.12) * k * taper;
    if (r < 0.4) return;
    // Four-point star: concave sides pulled in to the centre.
    ctx.moveTo(sx, sy - r);
    ctx.quadraticCurveTo(sx, sy, sx + r, sy);
    ctx.quadraticCurveTo(sx, sy, sx, sy + r);
    ctx.quadraticCurveTo(sx, sy, sx - r, sy);
    ctx.quadraticCurveTo(sx, sy, sx, sy - r);
  });
  ctx.fill();
  ctx.globalAlpha = a;
}

/** Flickering embers rising off the body: the flame skin, and much stronger for the fire power-up. */
function drawEmbers(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number, spacing: number, size: number) {
  const a = ctx.globalAlpha;
  const now = o.now;
  const clear = (u: number) => u > c.front + 0.6 && u < c.back - 0.25;
  c.marks(spacing, o.flow ?? 0, (u, id) => {
    if (!clear(u)) return;
    c.place(u, scratch);
    const { x, y } = scratch;
    const h = hash(id * 31);
    const phase = (now / 520 + h) % 1;
    ctx.globalAlpha = a * (1 - phase) * 0.8;
    ctx.fillStyle = phase < 0.4 ? '#fff3b0' : size > 1 ? '#ff7a1a' : o.skin.glow;
    ctx.beginPath();
    ctx.arc(x + (h - 0.5) * w * 0.5 * size, y - phase * w * 0.9 * size, w * 0.1 * size * (1 - phase * 0.5), 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = a;
}

/** A glint that runs down the body every few seconds. */
function drawSheen(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const cycle = (o.now + (o.seed ?? 0) * 397) % 2800;
  if (cycle > 1100) return;
  const len = c.back - c.front;
  const u = c.front - 1 + (cycle / 1100) * (len + 2);
  const lo = Math.max(c.front, u - 0.9);
  const hi = Math.min(c.back, u + 0.9);
  if (hi <= lo) return;
  const a = ctx.globalAlpha;
  const k = c.taper((lo + hi) / 2);
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#ffffff';
  for (const [alpha, width] of [
    [0.16, 0.85],
    [0.5, 0.3],
  ] as const) {
    ctx.globalAlpha = a * alpha;
    ctx.lineWidth = w * width * k;
    ctx.beginPath();
    c.trace(ctx, lo, hi);
    ctx.stroke();
  }
  ctx.globalAlpha = a;
}

/** Overlapping scales in staggered rows, their round edges toward the tail; they ride along with the body. */
function drawScales(ctx: CanvasRenderingContext2D, c: Curve, w: number, flow: number, color: string, alpha: number, spacing: number) {
  const a = ctx.globalAlpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, w * 0.07);
  ctx.lineCap = 'round';
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  c.marks(spacing, flow, (u, id) => {
    if (u <= c.front + 0.6 || u >= c.back - 0.25) return;
    c.place(u, scratch);
    const { x, y, dx, dy } = scratch;
    const k = c.taper(u);
    const r = w * 0.2 * k;
    const back = Math.atan2(-dy, -dx);
    // Rows alternate between one scale in the middle and two either side (no per-mark arrays: this runs thousands of times a frame).
    const odd = id & 1;
    for (let j = odd ? 0 : -1; j <= (odd ? 0 : 1); j += 2) {
      const off = j * 0.24;
      const sx = x - dy * off * w * k;
      const sy = y + dx * off * w * k;
      ctx.moveTo(sx + Math.cos(back - 1.2) * r, sy + Math.sin(back - 1.2) * r);
      ctx.arc(sx, sy, r, back - 1.2, back + 1.2);
    }
  });
  ctx.stroke();
  ctx.globalAlpha = a;
}

function drawPattern(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const { skin, now } = o;
  const flow = o.flow ?? 0;
  const a = ctx.globalAlpha;
  // Marks start a little behind the head, which covers the first half cell.
  const clear = (u: number) => u > c.front + 0.6 && u < c.back - 0.25;
  switch (skin.pattern) {
    case 'stripes': {
      // Dark chevrons across the body, pointing at the head.
      ctx.strokeStyle = darken(skin.body[1], 0.45);
      ctx.globalAlpha = a * 0.7;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      c.marks(1.25, flow, (u) => {
        if (!clear(u)) return;
        c.place(u, scratch);
        const { x, y, dx, dy } = scratch;
        const k = c.taper(u);
        const half = (w / 2) * 0.72 * k;
        ctx.lineWidth = Math.max(1.5, w * 0.2 * k);
        ctx.beginPath();
        ctx.moveTo(x - dy * half - dx * w * 0.14, y + dx * half - dy * w * 0.14);
        ctx.lineTo(x + dx * w * 0.1, y + dy * w * 0.1);
        ctx.lineTo(x + dy * half - dx * w * 0.14, y - dx * half - dy * w * 0.14);
        ctx.stroke();
      });
      break;
    }
    case 'dots': {
      ctx.fillStyle = lighten(skin.head, 0.2);
      ctx.globalAlpha = a * 0.75;
      ctx.beginPath();
      c.marks(1, flow, (u, id) => {
        if (!clear(u)) return;
        c.place(u, scratch);
        const { x, y, dx, dy } = scratch;
        const k = c.taper(u);
        // Alternating sides, like a spotted snake.
        const side = (id & 1 ? 1 : -1) * w * 0.17 * k;
        const r = w * 0.14 * k;
        ctx.moveTo(x - dy * side + r, y + dx * side);
        ctx.arc(x - dy * side, y + dx * side, r, 0, Math.PI * 2);
      });
      ctx.fill();
      break;
    }
    case 'scales':
      drawScales(ctx, c, w, flow, darken(skin.body[0], 0.4), a * 0.55, 0.42);
      break;
    case 'viper': {
      // Russell's viper: a chain of dark oval blotches down the back, each ringed black then pale,
      // with smaller dark spots alternating down the flanks.
      const ringPale = lighten(skin.head, 0.5);
      const fill = darken(skin.body[1], 0.25);
      c.marks(1.5, flow, (u, id) => {
        if (!clear(u)) return;
        c.place(u, scratch);
        const { x, y, dx, dy } = scratch;
        const k = c.taper(u);
        const along = w * 0.36 * k;
        const across = w * 0.25 * k;
        const ang = Math.atan2(dy, dx);
        for (const [color, grow] of [
          [ringPale, w * 0.07],
          ['#1a0f05', w * 0.04],
          [fill, 0],
        ] as const) {
          ctx.fillStyle = color;
          ctx.globalAlpha = a * (color === ringPale ? 0.8 : 0.95);
          ctx.beginPath();
          ctx.ellipse(x, y, along + grow * k, across + grow * k, ang, 0, Math.PI * 2);
          ctx.fill();
        }
        const side = (id & 1 ? 1 : -1) * w * 0.4 * k;
        ctx.fillStyle = fill;
        ctx.globalAlpha = a * 0.8;
        ctx.beginPath();
        ctx.arc(x - dy * side + dx * w * 0.5, y + dx * side + dy * w * 0.5, w * 0.09 * k, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = a;
      break;
    }
    case 'neon': {
      // Bright core line, with pulses of light racing down it.
      ctx.globalAlpha = a * 0.85;
      strokeBody(ctx, c, w * 0.22, lighten(skin.head, 0.3), 0.4);
      if (o.reduceMotion) break;
      const len = c.back - c.front;
      ctx.fillStyle = lighten(skin.glow, 0.5);
      for (let j = 0; j < 3; j++) {
        const u = c.front + ((now / 1000) * 9 + (j * len) / 3) % Math.max(1, len);
        c.place(u, scratch);
        const { x, y } = scratch;
        const r = w * 0.3 * c.taper(u);
        ctx.globalAlpha = a * 0.3;
        ctx.beginPath();
        ctx.arc(x, y, r * 1.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = a * 0.9;
        ctx.beginPath();
        ctx.arc(x, y, r * 0.7, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'galaxy': {
      // Twinkling stars that ride along with the body.
      c.marks(0.5, flow, (u, id) => {
        if (!clear(u)) return;
        c.place(u, scratch);
        const { x, y, dx, dy } = scratch;
        const k = c.taper(u);
        for (let s = 0; s < 2; s++) {
          const h1 = hash(id * 7 + s);
          const h2 = hash(id * 13 + s + 99);
          const tw = o.reduceMotion ? 0.8 : 0.45 + 0.55 * Math.abs(Math.sin(now / 380 + h1 * 9));
          const across = (h1 - 0.5) * w * 0.6 * k;
          const along = (h2 - 0.5) * w * 0.4;
          ctx.globalAlpha = a * tw;
          ctx.fillStyle = s ? '#ffffff' : lighten(skin.glow, 0.4);
          ctx.beginPath();
          ctx.arc(x - dy * across + dx * along, y + dx * across + dy * along, w * (0.05 + h2 * 0.06) * k, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      break;
    }
    case 'flame':
      if (!o.reduceMotion) drawEmbers(ctx, c, o, w, 1, 1);
      break;
    case 'lights':
      drawLights(ctx, c, o, w);
      break;
    case 'dragon': {
      // Big golden-edged scales, a row of golden spines down the back (pointing to the tail), and (your own
      // snake and previews: they cost a mark each) embers drifting up.
      drawScales(ctx, c, w, flow, DRAGON_GOLD, a * 0.5, 0.85);
      ctx.fillStyle = DRAGON_GOLD;
      ctx.globalAlpha = a * 0.9;
      ctx.beginPath();
      c.marks(1.1, flow, (u) => {
        if (!clear(u)) return;
        c.place(u, scratch);
        const { x, y, dx, dy } = scratch;
        const k = c.taper(u);
        const half = w * 0.12 * k;
        const len = w * 0.4 * k;
        ctx.moveTo(x + dx * len * 0.5 - dy * half, y + dy * len * 0.5 + dx * half);
        ctx.lineTo(x - dx * len * 0.5, y - dy * len * 0.5);
        ctx.lineTo(x + dx * len * 0.5 + dy * half, y + dy * len * 0.5 - dx * half);
        ctx.closePath();
      });
      ctx.fill();
      if (!o.reduceMotion && o.texture !== false) drawEmbers(ctx, c, o, w, 2.2, 0.85);
      break;
    }
    case 'storm':
      drawStorm(ctx, c, o, w);
      break;
    default:
      break;
  }
  ctx.globalAlpha = a;
  ctx.lineJoin = 'round';
}

const DRAGON_GOLD = '#ffcf5c';
/** Diwali bulbs, in turn down the string: marigold, gold, warm white, rose; and the bright core of each. */
const BULBS = ['#ff9f1c', '#ffd166', '#fff1d0', '#ff5fa2'];
const BULB_CORES = BULBS.map((b) => lighten(b, 0.6));
/** One frame's bulbs (x, y, size, color), so each color goes in one path without allocating. */
const bulbs = new Float32Array(4 * 1024);

/**
 * Diwali: a string of little lights zigzagging down the back, with a wave of brightness chasing along it.
 * Every bulb of a color goes into one path: two fills a color (halo, core) and one stroke for the string.
 */
function drawLights(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const a = ctx.globalAlpha;
  let n = 0;
  c.marks(0.8, o.flow ?? 0, (u, id) => {
    if (u <= c.front + 0.6 || u >= c.back - 0.25 || n >= 1024) return;
    c.place(u, scratch);
    const { x, y, dx, dy } = scratch;
    const k = c.taper(u);
    const across = (id & 1 ? 1 : -1) * w * 0.2 * k;
    const lit = o.reduceMotion ? 0.8 : 0.5 + 0.5 * Math.sin(o.now / 160 - id * 0.8);
    const i = n++ * 4;
    bulbs[i] = x - dy * across;
    bulbs[i + 1] = y + dx * across;
    bulbs[i + 2] = k * (0.55 + 0.45 * lit);
    bulbs[i + 3] = ((id % BULBS.length) + BULBS.length) % BULBS.length;
  });
  if (!n) return;
  // The string the bulbs hang on (a new piece wherever the body jumps: wrap-around, teleport).
  ctx.strokeStyle = '#ffd166';
  ctx.globalAlpha = a * 0.45;
  ctx.lineWidth = Math.max(1, w * 0.05);
  ctx.beginPath();
  const jump = (o.cs * 1.6) ** 2;
  for (let j = 0; j < n; j++) {
    const x = bulbs[j * 4]!;
    const y = bulbs[j * 4 + 1]!;
    if (j && (x - bulbs[j * 4 - 4]!) ** 2 + (y - bulbs[j * 4 - 3]!) ** 2 < jump) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  }
  ctx.stroke();
  for (const [layer, alpha, size] of LIGHT_LAYERS) {
    ctx.globalAlpha = a * alpha;
    for (let col = 0; col < BULBS.length; col++) {
      ctx.fillStyle = layer === 0 ? BULBS[col]! : BULB_CORES[col]!;
      ctx.beginPath();
      for (let j = 0; j < n; j++) {
        if (bulbs[j * 4 + 3] !== col) continue;
        const x = bulbs[j * 4]!;
        const y = bulbs[j * 4 + 1]!;
        const r = w * size * bulbs[j * 4 + 2]!;
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, Math.PI * 2);
      }
      ctx.fill();
    }
  }
  ctx.globalAlpha = a;
}
/** Halo, then core: [layer, alpha, radius as a share of the body]. */
const LIGHT_LAYERS = [
  [0, 0.45, 0.3],
  [1, 1, 0.12],
] as const;

/**
 * Thunder: a bolt of lightning crackling down the body, taking a new shape about eleven times a second
 * (one shape per 90 ms, not per frame, reads as crackle rather than noise). Two strokes of one path.
 */
function drawStorm(ctx: CanvasRenderingContext2D, c: Curve, o: SnakeArtOptions, w: number) {
  const a = ctx.globalAlpha;
  const crackle = o.reduceMotion ? 0 : Math.floor(o.now / 90);
  const jump = (o.cs * 1.6) ** 2;
  let n = 0;
  let lx = 0;
  let ly = 0;
  ctx.beginPath();
  c.marks(0.6, o.flow ?? 0, (u, id) => {
    if (u <= c.front + 0.6 || u >= c.back - 0.35) return;
    c.place(u, scratch);
    const { x, y, dx, dy } = scratch;
    const across = (hash(id * 7919 + crackle * 31 + (o.seed ?? 0)) - 0.5) * w * 0.5 * c.taper(u);
    const px = x - dy * across;
    const py = y + dx * across;
    if (n++ && (px - lx) ** 2 + (py - ly) ** 2 < jump) ctx.lineTo(px, py);
    else ctx.moveTo(px, py);
    lx = px;
    ly = py;
  });
  if (!n) return;
  ctx.lineJoin = 'miter';
  ctx.lineCap = 'round';
  ctx.strokeStyle = o.skin.glow;
  ctx.globalAlpha = a * 0.45;
  ctx.lineWidth = Math.max(1.5, w * 0.24);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.globalAlpha = a * 0.95;
  ctx.lineWidth = Math.max(1, w * 0.08);
  ctx.stroke();
  ctx.globalAlpha = a;
}

/**
 * Inside the open jaws: a dark throat with a red mouth inside it, and a fang hanging off each jaw
 * near the tip, angled into the gap. The mouth runs from the apex (`apx`, `apy`) out to the tips of
 * the upper and lower jaw.
 */
function drawMouth(ctx: CanvasRenderingContext2D, apx: number, apy: number, upper: Pt, lower: Pt, open: number, r: number, rim: number, skin: SkinDef) {
  const a = ctx.globalAlpha;
  /** `k` of the way from the apex to `p`. */
  const toward = (p: Pt, k: number): Pt => [apx + (p[0] - apx) * k, apy + (p[1] - apy) * k];
  const mid: Pt = [(upper[0] + lower[0]) / 2, (upper[1] + lower[1]) / 2];
  const between = (p: Pt, k: number): Pt => [mid[0] + (p[0] - mid[0]) * k, mid[1] + (p[1] - mid[1]) * k];
  // Throat, then the mouth's red lining a little inside it.
  ctx.fillStyle = '#2a0410';
  ctx.beginPath();
  ctx.moveTo(apx, apy);
  ctx.lineTo(...toward(upper, 0.92));
  ctx.lineTo(...toward(lower, 0.92));
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#e0284f';
  ctx.globalAlpha = a * 0.9;
  ctx.beginPath();
  ctx.moveTo(...toward(mid, 0.18));
  ctx.lineTo(...toward(between(upper, 0.6), 0.62));
  ctx.lineTo(...toward(between(lower, 0.6), 0.62));
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = a;
  // Lips: the jaw edges get the head's outline.
  ctx.strokeStyle = darken(skin.body[1], 0.55);
  ctx.lineWidth = rim;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(...upper);
  ctx.lineTo(apx, apy);
  ctx.lineTo(...lower);
  ctx.stroke();
  // Fangs: from each jaw edge near the tip, pointing into the mouth and a little back.
  const fang = r * (0.24 + 0.16 * open);
  ctx.fillStyle = '#fbf8ee';
  ctx.strokeStyle = 'rgba(40,10,20,0.6)';
  ctx.lineWidth = Math.max(0.6, rim * 0.5);
  for (const [edge, other] of [
    [upper, lower],
    [lower, upper],
  ] as const) {
    // Base on the jaw edge, 62-84% of the way out; tip toward the other jaw.
    const b0 = toward(edge, 0.62);
    const b1 = toward(edge, 0.84);
    const mx = (b0[0] + b1[0]) / 2;
    const my = (b0[1] + b1[1]) / 2;
    const tx = (other[0] + edge[0]) / 2 - mx;
    const ty = (other[1] + edge[1]) / 2 - my;
    const tl = Math.hypot(tx, ty) || 1;
    ctx.beginPath();
    ctx.moveTo(...b0);
    ctx.lineTo(...b1);
    ctx.lineTo(mx + (tx / tl) * fang - (b1[0] - b0[0]) * 0.25, my + (ty / tl) * fang - (b1[1] - b0[1]) * 0.25);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * A viper's head outline, in head space (forward, side) as shares of its length and width: a blunt
 * snout, jowls flaring out wide behind the eyes, narrowing again to the neck. Drawn through the
 * midpoints of these corners, so every corner is rounded.
 */
const VIPER_HEAD: Pt[] = [
  [1.1, 0],
  [0.9, 0.46],
  [-0.12, 1.24],
  [-0.74, 0.66],
  [-0.92, 0],
  [-0.74, -0.66],
  [-0.12, -1.24],
  [0.9, -0.46],
];

type HeadPoint = (f: number, s: number) => Pt;

/**
 * A viper's head markings. Russell's viper (the `viper` pattern): a dark V on the crown pointing at
 * the snout, and a dark stripe from each eye back to the jaw, all edged pale. Other vipers: just a
 * pale stripe behind each eye.
 */
function drawViperMarkings(ctx: CanvasRenderingContext2D, hp: HeadPoint, len: number, wid: number, r: number, skin: SkinDef) {
  const a = ctx.globalAlpha;
  const dark = darken(skin.body[1], 0.35);
  const pale = lighten(skin.head, 0.55);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const stroke = (pts: Pt[], color: string, width: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(...pts[0]!);
    for (const q of pts.slice(1)) ctx.lineTo(...q);
    ctx.stroke();
  };
  const stripes = [-1, 1].map((side) => [hp(len * 0.02, side * wid * 0.8), hp(-len * 0.42, side * wid * 0.92)]);
  if (skin.pattern !== 'viper') {
    ctx.globalAlpha = a * 0.75;
    for (const st of stripes) stroke(st, pale, Math.max(1, r * 0.09));
    ctx.globalAlpha = a;
    return;
  }
  const vee = [hp(-len * 0.62, wid * 0.4), hp(len * 0.12, 0), hp(-len * 0.62, -wid * 0.4)];
  ctx.globalAlpha = a * 0.85;
  for (const [pts, w] of [[vee, 0.13], ...stripes.map((st) => [st, 0.11] as const)] as const) {
    stroke(pts as Pt[], pale, Math.max(1.6, r * (w + 0.07)));
    stroke(pts as Pt[], dark, Math.max(1, r * w));
  }
  ctx.globalAlpha = a;
}

/** A viper's eyes: set wide on the jowls, golden irises with an upright slit pupil, under a scowling brow. */
function drawViperEyes(ctx: CanvasRenderingContext2D, hp: HeadPoint, len: number, wid: number, r: number) {
  const er = r * 0.27;
  for (const side of [-1, 1]) {
    const [x, y] = hp(len * 0.26, side * wid * 0.64);
    // Unit vectors: forward along the head, and outward to this eye's side.
    const [fx, fy] = hp(len * 0.26 + 1, side * wid * 0.64);
    const ux = fx - x;
    const uy = fy - y;
    const ox = -uy * side;
    const oy = ux * side;
    const g = ctx.createRadialGradient(x - er * 0.3, y - er * 0.3, er * 0.1, x, y, er);
    g.addColorStop(0, '#ffe58f');
    g.addColorStop(1, '#c58a1f');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, er, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#1a0f05';
    ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.stroke();
    // Slit pupil, upright as seen from the snake's side: across the head, looking a touch forward.
    ctx.fillStyle = '#0b0b14';
    ctx.beginPath();
    ctx.ellipse(x + ux * er * 0.15, y + uy * er * 0.15, er * 0.2, er * 0.82, Math.atan2(uy, ux), 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(x - er * 0.38, y - er * 0.38, er * 0.17, 0, Math.PI * 2);
    ctx.fill();
    // Brow: a dark lid over the inner rim of the eye, lower toward the snout, so the snake scowls.
    ctx.fillStyle = darken('#3a2410', 0.2);
    ctx.beginPath();
    const at = Math.atan2(-oy, -ox);
    ctx.arc(x, y, er * 1.08, at - 1.35, at + 1.35);
    ctx.lineTo(x - ox * er * 0.35 + ux * er * 0.55, y - oy * er * 0.35 + uy * er * 0.55);
    ctx.lineTo(x - ox * er * 0.55 - ux * er * 0.2, y - oy * er * 0.55 - uy * er * 0.2);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * The round (default) head's outline, in head space like VIPER_HEAD: an egg, widest just behind the eyes
 * and narrowing to a rounded snout, sampled finely enough to be smooth at any size.
 */
const ROUND_HEAD: Pt[] = Array.from({ length: 32 }, (_, i) => {
  const t = (i / 32) * Math.PI * 2;
  return [Math.cos(t), Math.sin(t) * (1 - 0.22 * Math.cos(t))] as Pt;
});

/** How long an eating head's chomp lasts (ms): it pops a size up, eyes wide, then settles. */
export const CHOMP_MS = 260;

/**
 * The round head's eyes: big and glossy, set forward on the head. A dark socket, a shaded white, an
 * iris in the skin's glow color around a round pupil, both looking at `look` (food, or the way ahead),
 * two catchlights, and an eyelid in the head's color that blinks (closing and opening smoothly).
 * Chomping, the pupils go wide.
 */
function drawRoundEyes(ctx: CanvasRenderingContext2D, hp: HeadPoint, len: number, wid: number, r: number, o: SnakeArtOptions, ux: number, uy: number, lid: number) {
  const { skin } = o;
  const high = o.quality === 'high';
  const er = r * 0.4;
  const wide = o.chomp !== undefined && o.chomp < 1 && !o.reduceMotion ? Math.sin(o.chomp * Math.PI) : 0;
  const lx = o.look?.[0] ?? ux;
  const ly = o.look?.[1] ?? uy;
  const socket = darken(skin.body[1], 0.6);
  const lidColor = mix(skin.head, skin.body[0], 0.35);
  for (const side of [-1, 1]) {
    const [x, y] = hp(len * 0.12, side * wid * 0.58);
    if (skin.signature === 'thunder' && high) {
      // Eyes glowing with the storm.
      ctx.fillStyle = rgba(skin.glow, 0.4);
      ctx.beginPath();
      ctx.arc(x, y, er * 1.9, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = socket;
    ctx.beginPath();
    ctx.arc(x, y, er * 1.13, 0, Math.PI * 2);
    ctx.fill();
    if (high) {
      const g = ctx.createRadialGradient(x - er * 0.25, y - er * 0.3, er * 0.2, x, y, er);
      g.addColorStop(0, '#ffffff');
      g.addColorStop(1, '#d5dde9');
      ctx.fillStyle = g;
    } else ctx.fillStyle = '#f4f7fb';
    ctx.beginPath();
    ctx.arc(x, y, er, 0, Math.PI * 2);
    ctx.fill();
    // Iris and pupil, looking the same way.
    const ix = x + lx * er * 0.2;
    const iy = y + ly * er * 0.2;
    const ir = er * 0.68;
    if (high) {
      const g = ctx.createRadialGradient(ix + er * 0.12, iy + er * 0.16, ir * 0.1, ix, iy, ir);
      g.addColorStop(0, lighten(skin.glow, 0.45));
      g.addColorStop(0.6, mix(skin.glow, skin.body[1], 0.35));
      g.addColorStop(1, darken(skin.body[1], 0.45));
      ctx.fillStyle = g;
    } else ctx.fillStyle = mix(skin.glow, skin.body[1], 0.35);
    ctx.beginPath();
    ctx.arc(ix, iy, ir, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0b0b14';
    ctx.beginPath();
    ctx.arc(x + lx * er * 0.28, y + ly * er * 0.28, er * (0.36 + 0.14 * wide), 0, Math.PI * 2);
    ctx.fill();
    // Catchlights: the light is at the top left, the same on both eyes.
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(x - er * 0.28, y - er * 0.32, er * 0.25, 0, Math.PI * 2);
    ctx.fill();
    if (high) {
      ctx.globalAlpha *= 0.85;
      ctx.beginPath();
      ctx.arc(x + er * 0.24, y + er * 0.26, er * 0.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha /= 0.85;
    }
    if (lid <= 0) continue;
    // The eyelid comes over from the back of the eye (the neck side): everything of the eye past a line across it is covered.
    const nx = -ux;
    const ny = -uy;
    const d = er * (1 - 2 * Math.min(1, lid));
    const big = er * 1.3;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, er * 1.02, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = lidColor;
    ctx.beginPath();
    ctx.moveTo(x + nx * d - ny * big, y + ny * d + nx * big);
    ctx.lineTo(x + nx * big - ny * big, y + ny * big + nx * big);
    ctx.lineTo(x + nx * big + ny * big, y + ny * big - nx * big);
    ctx.lineTo(x + nx * d + ny * big, y + ny * d - nx * big);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = socket;
    ctx.lineWidth = Math.max(1, er * 0.12);
    ctx.beginPath();
    ctx.moveTo(x + nx * d - ny * big, y + ny * d + nx * big);
    ctx.lineTo(x + nx * d + ny * big, y + ny * d - nx * big);
    ctx.stroke();
    ctx.restore();
  }
}

/** How far the eyelids are down (0 open .. 1 shut): a smooth blink every few seconds. */
function eyelid(o: SnakeArtOptions): number {
  if (o.reduceMotion) return 0;
  const seed = o.seed ?? 0;
  const t = (o.now + seed * 1531) % (3800 + (seed % 7) * 260);
  // Close over 70 ms, stay shut 40, open over 110.
  const blink = t < 70 ? t / 70 : t < 110 ? 1 : t < 220 ? 1 - (t - 110) / 110 : 0;
  return blink;
}

function drawHead(ctx: CanvasRenderingContext2D, hx: number, hy: number, o: SnakeArtOptions) {
  const { skin, cs, dir, now } = o;
  // Chomping: the head pops a size up and settles back.
  const pop = o.chomp !== undefined && o.chomp < 1 && !o.reduceMotion ? 1 + 0.14 * Math.sin(o.chomp * Math.PI) : 1;
  const viper = skin.headShape === 'viper';
  const r = cs * (viper ? HEAD : ROUND_HEAD_R) * pop;
  const ux = o.heading?.[0] ?? DX[dir]!;
  const uy = o.heading?.[1] ?? DY[dir]!;
  const px = -uy;
  const py = ux;
  const seed = o.seed ?? 0;
  const high = o.quality === 'high';
  const a = ctx.globalAlpha;
  // The skull sits a little forward of the neck and is longer than it is wide.
  const cx = hx + ux * r * 0.1;
  const cy = hy + uy * r * 0.1;
  const len = r * (viper ? 1.08 : 0.96);
  const wid = r * (viper ? 0.94 : 0.9);
  // Open jaws: a wedge cut out of the snout, from a little in front of the skull's centre.
  const open = Math.max(0, Math.min(1, o.mouth ?? 0));
  const jaw = open > 0.03 ? open * (viper ? 0.8 : 0.9) : 0;
  const back = viper ? 0.16 : 0.12;
  const apx = cx - ux * len * back;
  const apy = cy - uy * len * back;
  const outline = viper ? VIPER_HEAD : ROUND_HEAD;
  /** A point in head space: `f` along the heading and `s` across it, from the skull's centre. */
  const hp = (f: number, s: number, sx = 0, sy = 0): Pt => [cx + ux * f + px * s + sx, cy + uy * f + py * s + sy];
  const headPath = (grow: number, sx = 0, sy = 0) => {
    const pts = outline.map(([f, s]) => hp(f * (len + grow), s * (wid + grow), sx, sy));
    const n = pts.length;
    if (!viper) {
      // Fine enough to join with straight lines.
      ctx.beginPath();
      ctx.moveTo(...pts[0]!);
      for (let i = 1; i < n; i++) ctx.lineTo(...pts[i]!);
      ctx.closePath();
      return;
    }
    const midpoint = (i: number): Pt => [(pts[i % n]![0] + pts[(i + 1) % n]![0]) / 2, (pts[i % n]![1] + pts[(i + 1) % n]![1]) / 2];
    ctx.beginPath();
    ctx.moveTo(...midpoint(n - 1));
    for (let i = 0; i < n; i++) ctx.quadraticCurveTo(pts[i]![0], pts[i]![1], ...midpoint(i));
    ctx.closePath();
  };
  // The jaw tips: rays at the jaw's angle (the mouth is clipped to the outline).
  const reach = len * 1.02;
  const jawTip = (side: number): Pt => hp(Math.cos(jaw) * reach - len * back, side * Math.sin(jaw) * reach);
  /** Fill the head's outline (grown by `grow`, shifted by sx, sy), less the open mouth. */
  const fillSkull = (grow: number, sx = 0, sy = 0) => {
    ctx.save();
    if (jaw) {
      // Everything but the mouth's wedge.
      const far = len * 4;
      ctx.beginPath();
      ctx.rect(cx - far, cy - far, far * 2, far * 2);
      ctx.moveTo(apx + sx, apy + sy);
      const [ax, ay] = hp(Math.cos(jaw) * far, Math.sin(jaw) * far, sx, sy);
      const [bx, by] = hp(Math.cos(jaw) * far, -Math.sin(jaw) * far, sx, sy);
      ctx.lineTo(ax - ux * len * back, ay - uy * len * back);
      ctx.lineTo(bx - ux * len * back, by - uy * len * back);
      ctx.closePath();
      ctx.clip('evenodd');
    }
    headPath(grow, sx, sy);
    ctx.fill();
    ctx.restore();
  };

  // Tongue: flicks out every second or so, its forked tips quivering. Drawn first, so it comes out from under the snout.
  // With reduced motion it just stays out, still, so the snake keeps its tongue.
  {
    const period = 1400 + (seed % 5) * 90;
    const k = o.reduceMotion ? 0.5 : ((now + seed * 977) % period) / 520;
    if (k < 1 && !jaw) {
      // Out fast, hold and quiver, back in.
      const out = o.reduceMotion ? r * 0.8 : Math.sin(Math.min(1, k * 1.15) * Math.PI) * r * 1.05;
      if (out > r * 0.05) {
        const wig = o.reduceMotion ? 0 : Math.sin(now / 35 + seed) * r * 0.08;
        const bx = cx + ux * len * 0.85;
        const by = cy + uy * len * 0.85;
        const ex = bx + ux * out + px * wig;
        const ey = by + uy * out + py * wig;
        const fork = r * (0.24 + (o.reduceMotion ? 0 : 0.06 * Math.sin(now / 50 + seed)));
        const lw = Math.max(1.4, cs * 0.075);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const [color, width] of [
          ['#5a0a1e', lw + Math.max(1.2, cs * 0.04) * 2],
          ['#ff3b5c', lw],
        ] as const) {
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.quadraticCurveTo(bx + ux * out * 0.5 - px * wig, by + uy * out * 0.5 - py * wig, ex, ey);
          ctx.lineTo(ex + (ux * 0.85 + px * 0.6) * fork, ey + (uy * 0.85 + py * 0.6) * fork);
          ctx.moveTo(ex, ey);
          ctx.lineTo(ex + (ux * 0.85 - px * 0.6) * fork, ey + (uy * 0.85 - py * 0.6) * fork);
          ctx.stroke();
        }
      }
    }
  }

  if (high) {
    ctx.fillStyle = '#000000';
    ctx.globalAlpha = a * SHADOW_ALPHA;
    fillSkull(0, cs * SHADOW_X, cs * SHADOW_Y);
    ctx.globalAlpha = a;
  }
  // Head: outlined, shaded with the light from the top left.
  const rim = Math.max(1, cs * 0.07);
  ctx.fillStyle = darken(skin.body[1], OUTLINE_SHADE);
  fillSkull(rim);
  if (high) {
    // A light rim round the bottom right, as on the body: the head's color goes on nudged toward the light.
    ctx.fillStyle = lighten(skin.body[0], 0.3);
    fillSkull(0);
  }
  // Lit like a sphere from the top left: bright crown, the head's color, then a shadow side (stronger in high quality).
  const g = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.42, r * 0.1, cx, cy, r * 1.1);
  g.addColorStop(0, lighten(skin.head, high ? 0.45 : 0.35));
  g.addColorStop(0.5, skin.head);
  g.addColorStop(1, high ? darken(mix(skin.head, skin.body[0], 0.6), 0.35) : mix(skin.head, skin.body[0], 0.6));
  ctx.fillStyle = g;
  if (high) fillSkull(-r * 0.05, -r * 0.04, -r * 0.05);
  else fillSkull(0);
  if (jaw) {
    ctx.save();
    headPath(rim);
    ctx.clip();
    drawMouth(ctx, apx, apy, jawTip(-1), jawTip(1), open, r, rim, skin);
    ctx.restore();
  }
  if (viper) drawViperMarkings(ctx, hp, len, wid, r, skin);
  else {
    // Marking on the back of the skull: a darker chevron pointing at the snout, like a real snake's.
    ctx.fillStyle = mix(skin.head, skin.body[1], 0.75);
    ctx.globalAlpha = a * 0.2;
    ctx.beginPath();
    ctx.moveTo(...hp(-len * 0.12, 0));
    ctx.lineTo(...hp(-len * 0.86, wid * 0.4));
    ctx.lineTo(...hp(-len * 0.55, 0));
    ctx.lineTo(...hp(-len * 0.86, -wid * 0.4));
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = a;
  }
  if (high) {
    // Glossy highlight on the crown of the head, with a small sharp glint in it.
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = a * 0.26;
    ctx.beginPath();
    ctx.ellipse(cx - r * 0.24, cy - r * 0.38, r * 0.44, r * 0.2, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = a * 0.7;
    ctx.beginPath();
    ctx.ellipse(cx - r * 0.34, cy - r * 0.46, r * 0.13, r * 0.07, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = a;
  }
  // Nostrils at the tip of the snout (the open mouth is where they'd be).
  ctx.fillStyle = darken(skin.body[1], 0.5);
  ctx.globalAlpha = a * 0.55;
  for (const side of jaw ? [] : [-1, 1]) {
    ctx.beginPath();
    if (viper) ctx.arc(...hp(len * 0.82, side * wid * 0.2), Math.max(1, r * 0.075), 0, Math.PI * 2);
    else ctx.ellipse(...hp(len * 0.84, side * wid * 0.24), Math.max(0.8, r * 0.07), Math.max(0.6, r * 0.045), Math.atan2(uy, ux) + side * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = a;

  if (skin.accessory === 'halo') drawHalo(ctx, cx, cy, r, skin.glow, now, o.reduceMotion);

  // Eyes (or shades).
  if (viper) drawViperEyes(ctx, hp, len, wid, r);
  else if (skin.accessory === 'shades') {
    const ex = (side: number) => hp(len * 0.16, side * wid * 0.54);
    ctx.fillStyle = '#0b0b14';
    ctx.strokeStyle = '#0b0b14';
    ctx.lineWidth = Math.max(1, r * 0.12);
    ctx.beginPath();
    ctx.moveTo(...ex(-1));
    ctx.lineTo(...ex(1));
    ctx.stroke();
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.arc(...ex(side), r * 0.36, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    for (const side of [-1, 1]) {
      const [x, y] = ex(side);
      ctx.beginPath();
      ctx.ellipse(x - r * 0.12, y - r * 0.13, r * 0.11, r * 0.06, -0.6, 0, Math.PI * 2);
      ctx.fill();
    }
  } else drawRoundEyes(ctx, hp, len, wid, r, o, ux, uy, eyelid(o));

  if (skin.accessory === 'crown') drawCrown(ctx, cx, cy - r * 0.95, r);
  else if (skin.accessory === 'horns') drawHorns(ctx, cx, cy, r, ux, uy, px, py);
  else if (skin.accessory === 'diya') drawDiya(ctx, cx, cy - r * 0.85, r, now, o.reduceMotion);
  else if (skin.accessory === 'dragon') {
    // Long whiskers from the snout, swaying, then golden horns.
    const sway = o.reduceMotion ? 0 : Math.sin(now / 260 + seed);
    ctx.lineCap = 'round';
    for (const side of [-1, 1]) {
      const from = hp(len * 0.82, side * wid * 0.3);
      const bend = hp(len * 0.98, side * wid * (0.95 + 0.08 * sway * side));
      const to = hp(len * 0.5, side * wid * (1.3 + 0.15 * sway * side));
      for (const [color, width] of WHISKER_STROKES) {
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, r * width);
        ctx.beginPath();
        ctx.moveTo(...from);
        ctx.quadraticCurveTo(...bend, ...to);
        ctx.stroke();
      }
    }
    drawHorns(ctx, cx, cy, r, ux, uy, px, py, DRAGON_HORN);
  }
}

/** Dark edge, then gold: [color, width as a share of the head]. */
const WHISKER_STROKES = [
  ['#4a1405', 0.1],
  [DRAGON_GOLD, 0.05],
] as const;
const DRAGON_HORN = { base: '#fff1b8', tip: '#d98e04', edge: '#5a2a00' };

/** Diwali: a little clay diya sitting on the head, its flame flickering in a warm glow. */
function drawDiya(ctx: CanvasRenderingContext2D, cx: number, by: number, r: number, now: number, still: boolean) {
  const a = ctx.globalAlpha;
  const w = r * 0.95;
  const h = r * 0.34;
  const flick = still ? 1 : 0.86 + 0.14 * Math.sin(now / 67) * Math.sin(now / 109);
  const sway = still ? 0 : Math.sin(now / 170) * r * 0.07;
  const fx = cx + w * 0.08;
  const fy = by - h;
  // Glow round the flame.
  ctx.globalAlpha = a * 0.3 * flick;
  ctx.fillStyle = '#ffb627';
  ctx.beginPath();
  ctx.arc(fx, fy - r * 0.3, r * 0.7, 0, Math.PI * 2);
  ctx.fill();
  // The bowl, its spout lifting to the right, with a gold rim.
  ctx.globalAlpha = a;
  ctx.fillStyle = '#c4521c';
  ctx.strokeStyle = '#5e2208';
  ctx.lineWidth = Math.max(1, r * 0.07);
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, by - h);
  ctx.quadraticCurveTo(cx - w / 2, by, cx, by);
  ctx.quadraticCurveTo(cx + w / 2, by, cx + w * 0.62, by - h * 1.2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = '#ffd166';
  ctx.lineWidth = Math.max(1, r * 0.08);
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, by - h);
  ctx.lineTo(cx + w * 0.62, by - h * 1.2);
  ctx.stroke();
  // Flame: a teardrop, orange round a white-hot core, its tip swaying.
  const tall = r * 0.75 * flick;
  for (const [color, k] of FLAME_LAYERS) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(fx + sway * 2 * k, fy - tall * k);
    ctx.quadraticCurveTo(fx + r * 0.32 * k, fy - tall * 0.35 * k, fx, fy);
    ctx.quadraticCurveTo(fx - r * 0.32 * k, fy - tall * 0.35 * k, fx + sway * 2 * k, fy - tall * k);
    ctx.fill();
  }
  ctx.globalAlpha = a;
}
/** Outer flame, then its core: [color, size]. */
const FLAME_LAYERS = [
  ['#ff8a1c', 1],
  ['#fff3b0', 0.55],
] as const;

export function drawCrown(ctx: CanvasRenderingContext2D, cx: number, by: number, r: number) {
  const w = r * 1.5;
  const h = r * 0.95;
  const x0 = cx - w / 2;
  ctx.fillStyle = '#ffd166';
  ctx.strokeStyle = '#8a5a00';
  ctx.lineWidth = Math.max(1, r * 0.08);
  ctx.beginPath();
  ctx.moveTo(x0, by);
  ctx.lineTo(x0, by - h * 0.55);
  ctx.lineTo(x0 + w * 0.25, by - h * 0.2);
  ctx.lineTo(cx, by - h);
  ctx.lineTo(x0 + w * 0.75, by - h * 0.2);
  ctx.lineTo(x0 + w, by - h * 0.55);
  ctx.lineTo(x0 + w, by);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ff4d6d';
  ctx.beginPath();
  ctx.arc(cx, by - h * 0.35, r * 0.11, 0, Math.PI * 2);
  ctx.fill();
}

const BONE_HORN = { base: '#f5e6c8', tip: '#7a5230', edge: '#3a2412' };

function drawHorns(ctx: CanvasRenderingContext2D, hx: number, hy: number, r: number, ux: number, uy: number, px: number, py: number, colors = BONE_HORN) {
  for (const side of [-1, 1]) {
    // From the back corners of the head, sweeping out and backward.
    const bx = hx - ux * r * 0.25 + px * side * r * 0.6;
    const by = hy - uy * r * 0.25 + py * side * r * 0.6;
    const tx = bx + px * side * r * 1.0 - ux * r * 0.95;
    const ty = by + py * side * r * 1.0 - uy * r * 0.95;
    const g = ctx.createLinearGradient(bx, by, tx, ty);
    g.addColorStop(0, colors.base);
    g.addColorStop(1, colors.tip);
    ctx.fillStyle = g;
    ctx.strokeStyle = colors.edge;
    ctx.lineWidth = Math.max(1, r * 0.07);
    ctx.beginPath();
    ctx.moveTo(bx - ux * r * 0.3, by - uy * r * 0.3);
    ctx.quadraticCurveTo(bx + px * side * r * 0.75 + ux * r * 0.1, by + py * side * r * 0.75 + uy * r * 0.1, tx, ty);
    ctx.lineTo(bx + ux * r * 0.3, by + uy * r * 0.3);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
}

function drawHalo(ctx: CanvasRenderingContext2D, hx: number, hy: number, r: number, color: string, now: number, still: boolean) {
  const a = ctx.globalAlpha;
  ctx.globalAlpha = a * (still ? 0.9 : 0.7 + 0.3 * Math.sin(now / 300));
  ctx.strokeStyle = lighten(color, 0.4);
  ctx.lineWidth = Math.max(1.5, r * 0.16);
  ctx.beginPath();
  ctx.ellipse(hx, hy - r * 1.15, r * 0.75, r * 0.25, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = a;
}

// ----- arena themes -----

/**
 * One repeating tile of the theme's texture (TILE x TILE squares), drawn at
 * device resolution. Use with createPattern; the caller scales the pattern by
 * 1/dpr so one tile covers TILE / GRID_SPLIT cells in CSS px.
 */
export const THEME_TILE = 4;
/** Background squares per cell side: 2 makes the snake and food span two squares, so they read bigger. */
export const GRID_SPLIT = 2;

export function themeTile(theme: ThemeDef, cs: number, dpr: number, bold = false): HTMLCanvasElement | OffscreenCanvas {
  const side = Math.max(4, Math.round((THEME_TILE * cs * dpr) / GRID_SPLIT));
  const c: HTMLCanvasElement | OffscreenCanvas =
    typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(side, side) : Object.assign(document.createElement('canvas'), { width: side, height: side });
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  const s = side / THEME_TILE; // one cell, device px
  const light = isLight(theme.background);
  const ink = light ? '#000000' : '#ffffff';
  const fine = Math.max(1, dpr);
  switch (theme.pattern ?? 'grid') {
    case 'plain':
      break;
    case 'checker': {
      // One game cell per square, so the board reads like a board. The background shows through the rest.
      const half = Math.round(side / 2);
      g.fillStyle = rgba(ink, light ? 0.024 : 0.03);
      g.fillRect(0, 0, half, half);
      g.fillRect(half, half, side - half, side - half);
      break;
    }
    case 'weave': {
      // Basket weave: blocks of 2x2 cells, three bars each, alternating direction.
      for (let by = 0; by < 2; by++) {
        for (let bx = 0; bx < 2; bx++) {
          const ox = bx * 2 * s;
          const oy = by * 2 * s;
          const horizontal = (bx + by) % 2 === 0;
          for (let k = 0; k < 3; k++) {
            const t = (2 * s) / 3;
            const pad = t * 0.14;
            const rx = horizontal ? ox + pad : ox + k * t + pad;
            const ry = horizontal ? oy + k * t + pad : oy + pad;
            const rw = horizontal ? 2 * s - pad * 2 : t - pad * 2;
            const rh = horizontal ? t - pad * 2 : 2 * s - pad * 2;
            g.fillStyle = rgba(ink, light ? 0.05 : 0.045);
            roundRect(g, rx, ry, rw, rh, Math.min(rw, rh) / 2);
            g.fill();
            g.fillStyle = rgba(ink, light ? 0.04 : 0.05);
            roundRect(g, rx, ry, rw, Math.max(fine, rh * 0.18), Math.min(rw, rh) / 2);
            g.fill();
          }
        }
      }
      break;
    }
    case 'tiles': {
      for (let y = 0; y < THEME_TILE; y++) {
        for (let x = 0; x < THEME_TILE; x++) {
          g.fillStyle = rgba(ink, (x + y) % 2 ? 0.035 : 0.06);
          roundRect(g, x * s + s * 0.06, y * s + s * 0.06, s * 0.88, s * 0.88, s * 0.18);
          g.fill();
        }
      }
      break;
    }
    case 'dots': {
      g.fillStyle = rgba(ink, 0.11);
      for (let y = 0; y < THEME_TILE; y++) {
        for (let x = 0; x < THEME_TILE; x++) {
          g.beginPath();
          g.arc(x * s, y * s, Math.max(fine, s * 0.07), 0, Math.PI * 2);
          g.fill();
        }
      }
      break;
    }
    case 'hex': {
      g.strokeStyle = rgba(theme.border, 0.16);
      g.lineWidth = fine;
      const R = side / 4;
      const hh = (Math.sqrt(3) / 2) * R;
      for (let row = -1; row <= 3; row++) {
        for (let col = -1; col <= 3; col++) {
          const cx = col * R * 1.5;
          const cy = row * hh * 2 + (col % 2 ? hh : 0);
          g.beginPath();
          for (let k = 0; k < 6; k++) {
            const ang = (Math.PI / 3) * k;
            const x = cx + Math.cos(ang) * R * 0.95;
            const y = cy + Math.sin(ang) * R * 0.95;
            if (k) g.lineTo(x, y);
            else g.moveTo(x, y);
          }
          g.closePath();
          g.stroke();
        }
      }
      break;
    }
    case 'stars': {
      for (let i = 0; i < 9; i++) {
        const x = hash(i * 3 + 1) * side;
        const y = hash(i * 7 + 5) * side;
        const r = Math.max(fine * 0.6, s * (0.03 + hash(i * 11) * 0.06));
        g.fillStyle = rgba(i % 3 ? '#ffffff' : theme.golden, 0.35 + hash(i) * 0.4);
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'waves': {
      g.strokeStyle = rgba(theme.border, 0.14);
      g.lineWidth = Math.max(fine, s * 0.06);
      for (let row = 0; row < THEME_TILE; row++) {
        const y0 = row * s + s / 2;
        g.beginPath();
        for (let x = 0; x <= side; x += Math.max(1, s / 8)) {
          const y = y0 + Math.sin((x / side) * Math.PI * 2) * s * 0.18;
          if (x) g.lineTo(x, y);
          else g.moveTo(x, y);
        }
        g.stroke();
      }
      break;
    }
    case 'neon': {
      g.strokeStyle = rgba(theme.border, 0.12);
      g.lineWidth = Math.max(fine * 2, s * 0.12);
      gridLines(g, side, s);
      g.strokeStyle = rgba(theme.border, 0.38);
      g.lineWidth = fine;
      gridLines(g, side, s);
      break;
    }
    default: {
      g.strokeStyle = theme.gridLine;
      g.lineWidth = fine;
      gridLines(g, side, s);
    }
  }
  // Draw the tile over itself: roughly doubles every pattern's opacity, whatever the theme.
  if (bold) g.drawImage(c as CanvasImageSource, 0, 0);
  return c;
}

function gridLines(g: CanvasRenderingContext2D, side: number, s: number) {
  g.beginPath();
  for (let i = 0; i < THEME_TILE; i++) {
    g.moveTo(i * s + 0.5, 0);
    g.lineTo(i * s + 0.5, side);
    g.moveTo(0, i * s + 0.5);
    g.lineTo(side, i * s + 0.5);
  }
  g.stroke();
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  if (g.roundRect) g.roundRect(x, y, w, h, r);
  else g.rect(x, y, w, h);
}

/** Background gradient for an arena of W x H CSS px: theme color in the middle fading to background2. */
export function arenaGradient(g: CanvasRenderingContext2D, theme: ThemeDef, W: number, H: number): CanvasGradient {
  const grad = g.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.1, W / 2, H / 2, Math.max(W, H) * 0.75);
  grad.addColorStop(0, theme.background);
  grad.addColorStop(1, theme.background2 ?? theme.background);
  return grad;
}

/** Glowing arena border, like a neon tube: a wide haze, a glow, the tube, and its hot white core. */
/**
 * The wall round the arena: a glow and a bright line. With `outside` (when there is room drawn past the
 * edge, as in the big arena's camera view) all of it sits outside the playable cells, so a snake running
 * along the last row isn't drawn on top of the wall's glow, looking as if it were on the wall.
 */
export function arenaBorder(g: CanvasRenderingContext2D, theme: ThemeDef, W: number, H: number, cs: number, outside = false) {
  /** A frame `lw` wide: centred on the edge, or just outside it. */
  const frame = (lw: number, inset: number) => {
    g.lineWidth = lw;
    if (outside) g.strokeRect(-lw / 2, -lw / 2, W + lw, H + lw);
    else g.strokeRect(inset, inset, W - inset * 2, H - inset * 2);
  };
  g.strokeStyle = rgba(theme.border, 0.08);
  frame(Math.max(12, cs * 1.6), 0);
  g.strokeStyle = rgba(theme.border, 0.25);
  frame(Math.max(6, cs * 0.6), 0);
  g.strokeStyle = theme.border;
  frame(Math.max(2, cs * 0.16), 1);
  g.strokeStyle = rgba('#ffffff', isLight(theme.background) ? 0.3 : 0.6);
  frame(Math.max(1, cs * 0.05), 1);
}
